export const KEYRUS_AI_SLUG = 'keyrus-ai'
export const KEYRUS_AI_ROOT_PATH = `/${KEYRUS_AI_SLUG}`
export const KEYRUS_AI_GATEWAY_COOKIE_NAME = 'keyrus_ai_seen'
export const KEYRUS_AI_GATEWAY_COOKIE_DAYS = 7
// Master switch for the cross-session "once per N days" cookie (impression-based,
// written by markKeyrusAiSeen below). OFF (current): no cookie is written;
// suppression is session-scoped only — the gateway shows once per session via
// sessionStorage (markKeyrusAiSeen / hasKeyrusAiSessionSeen), then the slim banner.
export const KEYRUS_AI_GATEWAY_COOKIE_ENABLED = false

// While the cookie is disabled we always force the modal (ignore the cookie).
// When enabled, the cookie governs visibility, but a build can still force the
// modal on every reload with GATSBY_KEYRUS_AI_GATEWAY_FORCE_SHOW=true (dev aid).
export const KEYRUS_AI_GATEWAY_FORCE_SHOW_ON_RELOAD =
  !KEYRUS_AI_GATEWAY_COOKIE_ENABLED ||
  process.env.GATSBY_KEYRUS_AI_GATEWAY_FORCE_SHOW === 'true'
export const KEYRUS_AI_BANNER_HEIGHT_CSS_VAR = '--keyrus-ai-banner-height'

// Below the lg breakpoint (1440) the centered KeyrusAiNav pill no longer has
// comfortable clearance from the fixed top-right "Continue to the Keyrus website"
// control and they collide (e.g. at >=120% zoom on a typical large screen). Below
// this width the nav collapses to its hamburger and the Continue control moves
// top-left — the same arrangement already used on mobile, just applied earlier.
export const KEYRUS_AI_NAV_COMPACT_QUERY = '(max-width:1439px)'

const DAY_IN_MS = 24 * 60 * 60 * 1000

export const KEYRUS_AI_SESSION_KEY = 'keyrus_ai_session_seen'

// "AI intro already seen this session." sessionStorage survives full reloads
// (incl. the edge 301 bounce through "/") but clears when the tab/session ends,
// so the gateway shows once per session. try/catch-guarded for private mode.
export const hasKeyrusAiSessionSeen = () => {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return window.sessionStorage.getItem(KEYRUS_AI_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

export const hasKeyrusAiSeenCookie = () => {
  if (typeof document === 'undefined') {
    return false
  }

  return document.cookie
    .split(';')
    .some(cookie =>
      cookie.trim().startsWith(`${KEYRUS_AI_GATEWAY_COOKIE_NAME}=`),
    )
}

// Record that the AI intro was seen this visit. Always sets the session flag;
// also writes the impression-based N-day cookie when the cookie is enabled
// (dormant by default). Called wherever the intro renders — the gateway modal
// AND the standalone /keyrus-ai page — so seeing it in either place counts once.
export const markKeyrusAiSeen = () => {
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(KEYRUS_AI_SESSION_KEY, '1')
    } catch {
      // private mode / blocked storage — fall back to the in-memory guard
    }
  }

  if (typeof document !== 'undefined' && KEYRUS_AI_GATEWAY_COOKIE_ENABLED) {
    const expires = new Date(
      Date.now() + KEYRUS_AI_GATEWAY_COOKIE_DAYS * DAY_IN_MS,
    ).toUTCString()

    document.cookie = `${KEYRUS_AI_GATEWAY_COOKIE_NAME}=1; expires=${expires}; path=/; SameSite=Lax`
  }
}

export const KEYRUS_AI_HUBSPOT_REGION = 'eu1'
export const KEYRUS_AI_HUBSPOT_PORTAL_ID = '25509106'
export const KEYRUS_AI_HUBSPOT_FORM_ID = 'e5689265-be7e-46bd-805b-21947250898f'

export const getKeyrusAiHubspotEndpoint = () =>
  `https://api-${KEYRUS_AI_HUBSPOT_REGION}.hsforms.com/submissions/v3/integration/submit/${KEYRUS_AI_HUBSPOT_PORTAL_ID}/${KEYRUS_AI_HUBSPOT_FORM_ID}`

// Lead country (HubSpot `leadcountry__c`) by URL countryCode. Aligned on the
// `salesforceCountry` field of packages/keyrus/static/countries/countriesByLocales.js.
// The gateway overlays every page (where the page GraphQL query does not always
// request `contentfulCountry.salesforce`), so we resolve the value from the
// countryCode here rather than relying on a per-page query.
const SALESFORCE_COUNTRY_BY_CODE = {
  worldwide: 'Group',
  fr: 'France',
  be: 'Belgium',
  sp: 'Spain',
  br: 'Brazil',
  latam: 'Colombia',
  mx: 'Mexico',
  pt: 'Portugal',
  uk: 'United Kingdom',
  us: 'United States',
  ca: 'Canada',
  za: 'South Africa',
  mea: 'Tunisia',
  cn: 'China',
  sea: 'Singapore',
  il: 'France',
}

export const KEYRUS_AI_DEFAULT_LEAD_COUNTRY = 'Group'

// Resolve the HubSpot lead-country value for the KeyrusAiContact form. Prefers a
// `salesforce` value already present on countryData (when a page happened to
// query it), then falls back to the countryCode map, then to Group (Worldwide).
export const getKeyrusAiLeadCountry = countryData => {
  const salesforce = countryData && countryData.salesforce

  if (salesforce) {
    return salesforce
  }

  const code = countryData && countryData.countryCode

  return SALESFORCE_COUNTRY_BY_CODE[code] || KEYRUS_AI_DEFAULT_LEAD_COUNTRY
}

const COPY = {
  en: {
    eyebrow: 'Keyrus AI',
    title: 'Keyrus AI — Architect of intelligence',
    description:
      'The future of business will run on intelligence, not software. Discover Keyrus AI.',
    discoverLabel: 'Discover Keyrus AI',
    continueLabel: 'Continue to the Keyrus website',
    closeLabel: 'Close Keyrus AI introduction',
    bannerText: 'Keyrus Ai: Architect of intelligence',
    bannerDescription: 'We operationalize intelligence',
    bannerCtaLabel: 'Explore our approach',
    pageIntro:
      'Keyrus is entering a new chapter: clearer in its market promise, more focused on measurable business outcomes, and still grounded in the data expertise clients know.',
    whyTitle: 'Why we are evolving',
    whyBody:
      'Organizations need partners who can connect strategy, data platforms, AI, and adoption. Our new positioning makes that promise easier to understand and easier to activate across every market we serve.',
    changesTitle: 'What changes',
    changes: [
      'A clearer story around data, AI, and transformation.',
      'A stronger focus on outcomes, from decision intelligence to operational performance.',
      'A more direct path from business ambition to the services, insights, and experts that can help.',
    ],
    continuityTitle: 'What remains',
    continuity: [
      'The same access to Keyrus services, insights, industries, and local teams.',
      'The same commitment to helping clients make data matter.',
      'The same website navigation after this short introduction.',
    ],
    nextTitle: 'What you can do next',
    nextBody:
      'Explore the new direction, continue to the services catalogue, read our latest perspectives, or contact a local Keyrus team.',
    servicesLabel: 'Explore services',
    insightsLabel: 'Read insights',
    contactLabel: 'Contact Keyrus',
  },
  fr: {
    eyebrow: 'Keyrus AI',
    title: 'Keyrus AI — Architect of intelligence',
    description:
      'The future of business will run on intelligence, not software. Discover Keyrus AI.',
    discoverLabel: 'Discover Keyrus AI',
    continueLabel: 'Continuer vers le site Keyrus',
    closeLabel: 'Close Keyrus AI introduction',
    bannerText: 'Keyrus Ai: Architect of intelligence',
    bannerDescription: 'Nous rendons l’intelligence opérationnelle',
    bannerCtaLabel: 'Explorer notre approche',
    pageIntro:
      'Keyrus ouvre un nouveau chapitre : une promesse de marche plus lisible, un focus renforce sur les resultats business et l expertise data qui fait notre force.',
    whyTitle: 'Pourquoi nous evoluons',
    whyBody:
      'Les organisations ont besoin de partenaires capables de connecter strategie, plateformes data, IA et adoption. Ce nouveau positionnement rend cette promesse plus claire et plus actionnable.',
    changesTitle: 'Ce qui change',
    changes: [
      'Une histoire plus claire autour de la data, de l IA et de la transformation.',
      'Un focus renforce sur les resultats mesurables et la performance operationnelle.',
      'Un acces plus direct aux services, insights et experts utiles a chaque ambition business.',
    ],
    continuityTitle: 'Ce qui reste',
    continuity: [
      'Le meme acces aux services, insights, industries et equipes locales Keyrus.',
      'Le meme engagement : aider nos clients a faire compter la data.',
      'La meme navigation sur le site apres cette courte introduction.',
    ],
    nextTitle: 'Et maintenant',
    nextBody:
      'Explorez notre nouvelle direction, parcourez nos services, lisez nos dernieres perspectives ou contactez une equipe Keyrus locale.',
    servicesLabel: 'Explorer les services',
    insightsLabel: 'Lire les insights',
    contactLabel: 'Contacter Keyrus',
  },
  es: {
    eyebrow: 'Keyrus AI',
    title: 'Keyrus AI — Architect of intelligence',
    description:
      'The future of business will run on intelligence, not software. Discover Keyrus AI.',
    discoverLabel: 'Discover Keyrus AI',
    continueLabel: 'Continuar al sitio Keyrus',
    closeLabel: 'Close Keyrus AI introduction',
    bannerText: 'Keyrus Ai: Architect of intelligence',
    bannerDescription: 'Hacemos operativa la inteligencia',
    bannerCtaLabel: 'Explora nuestro enfoque',
    pageIntro:
      'Keyrus abre una nueva etapa: una promesa mas clara, mayor foco en resultados de negocio y la misma experiencia en datos que nuestros clientes conocen.',
    whyTitle: 'Por que evolucionamos',
    whyBody:
      'Las organizaciones necesitan socios capaces de conectar estrategia, plataformas de datos, IA y adopcion. Nuestro nuevo posicionamiento hace esa promesa mas clara y accionable.',
    changesTitle: 'Que cambia',
    changes: [
      'Una historia mas clara sobre datos, IA y transformacion.',
      'Mayor foco en resultados medibles y rendimiento operativo.',
      'Un camino mas directo hacia servicios, insights y expertos relevantes.',
    ],
    continuityTitle: 'Que se mantiene',
    continuity: [
      'El mismo acceso a servicios, insights, industrias y equipos locales de Keyrus.',
      'El mismo compromiso con ayudar a los clientes a hacer que los datos importen.',
      'La misma navegacion del sitio despues de esta breve introduccion.',
    ],
    nextTitle: 'Que puedes hacer ahora',
    nextBody:
      'Explora la nueva direccion, revisa nuestros servicios, lee nuestras perspectivas o contacta con un equipo local de Keyrus.',
    servicesLabel: 'Explorar servicios',
    insightsLabel: 'Leer insights',
    contactLabel: 'Contactar Keyrus',
  },
  pt: {
    eyebrow: 'Keyrus AI',
    title: 'Keyrus AI — Architect of intelligence',
    description:
      'The future of business will run on intelligence, not software. Discover Keyrus AI.',
    discoverLabel: 'Discover Keyrus AI',
    continueLabel: 'Acessar o site da Keyrus',
    closeLabel: 'Close Keyrus AI introduction',
    bannerText: 'Keyrus Ai: Architect of intelligence',
    bannerDescription: 'Operacionalizamos a inteligência',
    bannerCtaLabel: 'Explore nossa abordagem',
    pageIntro:
      'A Keyrus inicia uma nova fase: uma promessa de mercado mais clara, foco maior em resultados de negocio e a mesma experiencia em dados que nossos clientes conhecem.',
    whyTitle: 'Por que estamos evoluindo',
    whyBody:
      'As organizacoes precisam de parceiros capazes de conectar estrategia, plataformas de dados, IA e adocao. Nosso novo posicionamento torna essa promessa mais clara e acionavel.',
    changesTitle: 'O que muda',
    changes: [
      'Uma narrativa mais clara sobre dados, IA e transformacao.',
      'Maior foco em resultados mensuraveis e desempenho operacional.',
      'Um caminho mais direto para servicos, insights e especialistas relevantes.',
    ],
    continuityTitle: 'O que permanece',
    continuity: [
      'O mesmo acesso a servicos, insights, industrias e equipes locais da Keyrus.',
      'O mesmo compromisso em ajudar clientes a fazer os dados importarem.',
      'A mesma navegacao do site depois desta breve introducao.',
    ],
    nextTitle: 'O que fazer agora',
    nextBody:
      'Explore a nova direcao, conheca nossos servicos, leia nossas perspectivas ou fale com uma equipe local da Keyrus.',
    servicesLabel: 'Explorar servicos',
    insightsLabel: 'Ler insights',
    contactLabel: 'Contactar Keyrus',
  },
}

export const getKeyrusAiLocaleKey = nodeLocale => {
  const family = String(nodeLocale || 'en')
    .split('-')[0]
    .toLowerCase()

  return ['fr', 'es', 'pt'].includes(family) ? family : 'en'
}

export const getKeyrusAiCopy = nodeLocale =>
  COPY[getKeyrusAiLocaleKey(nodeLocale)]

export const getLocaleSegment = country => {
  if (!country || country.technicalName === 'Worldwide') {
    return ''
  }

  const locale = country.navLocale
    ? String(country.navLocale).replace(/^\/+|\/+$/g, '')
    : String(country.localeCode || '')
        .toLowerCase()
        .slice(0, 2)

  return locale ? `${locale}/` : ''
}

export const getKeyrusAiPath = country => {
  if (!country || country.technicalName === 'Worldwide') {
    return KEYRUS_AI_ROOT_PATH
  }

  return `/${country.countryCode}/${getLocaleSegment(country)}${KEYRUS_AI_SLUG}`
}

export const getCountryPath = (country, slug) => {
  if (!country) {
    return `/worldwide/${slug}`
  }

  return `/${country.countryCode}/${getLocaleSegment(country)}${slug}`
}

export const getKeyrusAiRouteCountry = pathname => {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)

  if (parts[parts.length - 1] !== KEYRUS_AI_SLUG) {
    return null
  }

  if (parts[0] === 'worldwide') {
    return {
      countryCode: 'worldwide',
      localeCode: 'en-US',
      navLocale: '',
      technicalName: 'Worldwide',
    }
  }

  if (parts.length < 3) {
    return null
  }

  return {
    countryCode: parts[0],
    localeCode: parts[1],
    navLocale: parts[1],
    technicalName: parts[0],
  }
}

export const isKeyrusAiPathname = pathname => {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)

  return parts[parts.length - 1] === KEYRUS_AI_SLUG
}

// Insight articles AND white papers both live under the `insights/` prefix
// (see keyrus SUB_LEVEL_PAGES). True only for detail pages (a slug follows
// `insights`), so the `/insights` listing page is unaffected. Used to suppress
// the AI gateway on deep links shared in campaigns, so they open the content
// directly instead of behind the gateway overlay.
export const isInsightArticlePathname = pathname => {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)
  const idx = parts.indexOf('insights')

  return idx !== -1 && idx < parts.length - 1
}

export const getKeyrusAiContinue = pathname => {
  const routeCountry = getKeyrusAiRouteCountry(pathname)

  if (routeCountry) {
    return {href: getCountryPath(routeCountry, 'home'), isExternal: false}
  }

  return {href: '/', isExternal: true}
}
